import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";

const selectMenuProps = {
  PaperProps: {
    style: {
      marginTop: "1.5px",
      maxHeight: 150,
      width: "auto",
      maxWidth: "100%",
    },
  },
};

const FormDetailsCard = ({
  selectedFile,
  onSubmit,
  onCancel,
  uploading,
  jurisdictionOptions = [],
  initialValues = {},
}) => {
  const validationSchema = Yup.object({
    state: Yup.string().required("State is required"),
    isStatewideForm: Yup.boolean().required(),
    county: Yup.string().when("isStatewideForm", {
      is: false,
      then: (schema) => schema.required("County is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    jurisdictionLevel: Yup.string().when("isStatewideForm", {
      is: false,
      then: (schema) =>
        schema
          .oneOf(["county", "municipality"])
          .required("Jurisdiction level is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    municipality: Yup.string().when(["isStatewideForm", "jurisdictionLevel"], {
      is: (isStatewideForm, jurisdictionLevel) =>
        !isStatewideForm && jurisdictionLevel === "municipality",
      then: (schema) => schema.required("Municipality is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return (
    <Box
      sx={{
        border: "1px solid #d3d3d3",
        p: 2,
        mt: 2,
        borderRadius: "10px",
        boxShadow: "2px 3px 2px 1px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        textAlign="center"
        variant="h6"
        color="primary"
        sx={{ mb: 2 }}
      >
        {selectedFile?.name}
      </Typography>

      <Formik
        initialValues={{
          state: initialValues.state || "fl",
          isStatewideForm:
            typeof initialValues.isStatewideForm === "boolean"
              ? initialValues.isStatewideForm
              : true,
          county: initialValues.county || "",
          jurisdictionLevel: initialValues.jurisdictionLevel || "county",
          municipality: initialValues.municipality || "",
          mun_type: initialValues.mun_type || "",
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Box component="form" onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <FormControl
                fullWidth
                error={touched.state && Boolean(errors.state)}
                variant="filled"
              >
                <InputLabel>State</InputLabel>
                <Select
                  name="state"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="State"
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="fl">Florida</MenuItem>
                </Select>
                <FormHelperText>{touched.state && errors.state}</FormHelperText>
              </FormControl>
              <FormControl fullWidth variant="filled">
                <InputLabel>Applies Statewide?</InputLabel>
                <Select
                  name="isStatewideForm"
                  label="Applies Statewide?"
                  value={values.isStatewideForm ? "yes" : "no"}
                  onChange={(e) => {
                    const isStatewideForm = e.target.value === "yes";
                    handleChange({
                      target: {
                        name: "isStatewideForm",
                        value: isStatewideForm,
                      },
                    });

                    if (isStatewideForm) {
                      setFieldValue("county", "");
                      setFieldValue("jurisdictionLevel", "county");
                      setFieldValue("municipality", "");
                      setFieldValue("mun_type", "");
                    }
                  }}
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>

              {!values.isStatewideForm && (
                <Autocomplete
                  options={jurisdictionOptions}
                  value={
                    jurisdictionOptions.find(
                      (item) => item.county_slug === values.county,
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    setFieldValue("county", newValue?.county_slug || "");
                    setFieldValue("municipality", "");
                    setFieldValue("mun_type", "");
                  }}
                  onBlur={() => setFieldTouched("county", true)}
                  getOptionLabel={(option) => option?.county_name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.county_slug === value?.county_slug
                  }
                  ListboxProps={{
                    style: {
                      maxHeight: 150,
                      overflowY: "auto",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="County"
                      variant="filled"
                      error={touched.county && Boolean(errors.county)}
                      helperText={touched.county && errors.county}
                      fullWidth
                    />
                  )}
                />
              )}

              {!values.isStatewideForm && (
                <FormControl fullWidth variant="filled">
                  <InputLabel>Jurisdiction level</InputLabel>
                  <Select
                    name="jurisdictionLevel"
                    label="Jurisdiction level"
                    value={values.jurisdictionLevel}
                    onChange={(e) => {
                      handleChange(e);
                      if (e.target.value !== "municipality") {
                        setFieldValue("municipality", "");
                        setFieldValue("mun_type", "");
                      }
                    }}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="county">County</MenuItem>
                    <MenuItem value="municipality">Municipality</MenuItem>
                  </Select>
                  <FormHelperText>
                    {touched.jurisdictionLevel && errors.jurisdictionLevel}
                  </FormHelperText>
                </FormControl>
              )}

              {!values.isStatewideForm &&
                values.jurisdictionLevel === "municipality" && (
                  <Autocomplete
                    options={
                      jurisdictionOptions.find(
                        (item) => item.county_slug === values.county,
                      )?.municipality || []
                    }
                    value={
                      (
                        jurisdictionOptions.find(
                          (item) => item.county_slug === values.county,
                        )?.municipality || []
                      ).find((item) => item.mun_slug === values.municipality) ||
                      null
                    }
                    onChange={(_, newValue) => {
                      setFieldValue("municipality", newValue?.mun_slug || "");
                      setFieldValue("mun_type", newValue?.mun_type || "");
                    }}
                    onBlur={() => setFieldTouched("municipality", true)}
                    getOptionLabel={(option) => option?.mun_name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.mun_slug === value?.mun_slug
                    }
                    ListboxProps={{
                      style: {
                        maxHeight: 150,
                        overflowY: "auto",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="municipality"
                        label="Municipality"
                        variant="filled"
                        error={
                          touched.municipality && Boolean(errors.municipality)
                        }
                        helperText={touched.municipality && errors.municipality}
                        fullWidth
                      />
                    )}
                  />
                )}
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 1 }}
            >
              <Button
                type="button"
                color="error"
                disabled={uploading}
                variant="text"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default FormDetailsCard;
